import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { PenSquare, Star, Reply, ListTodo, ArrowLeft, Inbox, Send, Loader2, Forward, Ticket, FolderTree, BadgeCheck, UserPlus, ChevronDown, ChevronRight, Building2, CalendarDays, Wand2, Sparkles, Trash2, X, Plus, FolderPlus, CheckSquare, FolderInput, Paperclip, Download, File as FileIcon, CheckCheck, Undo2, Mail, RefreshCw, Settings } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useRealtime } from "../context/RealtimeContext";
import { useCompany } from "../context/CompanyContext";
import { Avatar } from "../components/Avatar";
import { EmailComposer, fmtSize } from "../components/EmailComposer";
import { EmailAccountsModal } from "../components/EmailAccountsModal";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";

const FOLDERS = [
  { id: "inbox", label: "Réception", icon: Inbox },
  { id: "processed", label: "Traités", icon: CheckCheck },
  { id: "sent", label: "Envoyés", icon: Send },
  { id: "starred", label: "Suivis", icon: Star },
];

function sortEmails(list, mode) {
  const arr = [...list];
  if (mode === "old") arr.sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
  else if (mode === "sender") arr.sort((a, b) => (a.from_name || a.from_email || "").localeCompare(b.from_name || b.from_email || ""));
  else if (mode === "unread") arr.sort((a, b) => (a.is_read === b.is_read ? (b.created_at || "").localeCompare(a.created_at || "") : (a.is_read ? 1 : -1)));
  else arr.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
  return arr;
}

function bucketByDate(sorted) {
  const now = new Date();
  const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const startYest = startToday - 86400000;
  const startWeek = startToday - 6 * 86400000;
  const defs = [
    { key: "today", label: "Aujourd'hui", items: [] },
    { key: "yesterday", label: "Hier", items: [] },
    { key: "week", label: "Cette semaine", items: [] },
    { key: "older", label: "Plus anciens", items: [] },
  ];
  sorted.forEach((e) => {
    const t = new Date(e.created_at || 0).getTime();
    if (t >= startToday) defs[0].items.push(e);
    else if (t >= startYest) defs[1].items.push(e);
    else if (t >= startWeek) defs[2].items.push(e);
    else defs[3].items.push(e);
  });
  return defs.filter((d) => d.items.length);
}

function timeAgo(iso) {
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "à l'instant";
  if (diff < 3600) return `${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} h`;
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" });
}

export default function MailPage() {
  const { user } = useAuth();
  const { events, refreshSummary } = useRealtime();
  const { selectedId, managed } = useCompany();
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    if (location.state?.compose) {
      setCompose(location.state.compose);
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);
  const [folder, setFolder] = useState("inbox");
  const [emails, setEmails] = useState([]);
  const [selected, setSelected] = useState(null);
  const [aiSummary, setAiSummary] = useState({ count: 0, points: [], loading: false, open: true });
  const [attachHub, setAttachHub] = useState(null); // null | {loading, items}

  const openAttachHub = async () => {
    setRailOpen(false);
    setAttachHub({ loading: true, items: [] });
    try {
      const params = { folder };
      if (selectedId && selectedId !== "all") params.company_id = selectedId;
      const { data } = await api.get("/emails/attachments", { params });
      setAttachHub({ loading: false, items: data || [] });
    } catch {
      setAttachHub({ loading: false, items: [] });
    }
  };
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [compose, setCompose] = useState(null); // {to,subject,body,in_reply_to}
  const [convert, setConvert] = useState(null); // task draft from email
  const [ticketDraft, setTicketDraft] = useState(null); // ticket draft from email
  const [smartFolders, setSmartFolders] = useState([]);
  const [clientFolders, setClientFolders] = useState([]);
  const [rules, setRules] = useState([]);
  const [ruleDialog, setRuleDialog] = useState(null);
  const [clientOpts, setClientOpts] = useState([]);
  const [manualFolders, setManualFolders] = useState([]);
  const [mfDialog, setMfDialog] = useState(null); // {id?,name,color}
  const [selectMode, setSelectMode] = useState(false);
  const longPressRef = useRef(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiSugg, setAiSugg] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [railOpen, setRailOpen] = useState(false);
  const [folderDialog, setFolderDialog] = useState(null); // {id?,name,emails:[],color}
  const [collapsedCo, setCollapsedCo] = useState({});
  const [sortMode, setSortMode] = useState("recent");
  const [groupMode, setGroupMode] = useState("date");
  const [railWidth, setRailWidth] = useState(() => Number(localStorage.getItem("fd_mail_rail_w")) || 220);
  const draggingRef = useRef(false);
  const railRef = useRef(null);
  const [showAccounts, setShowAccounts] = useState(false);
  const [syncingAll, setSyncingAll] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [acctMenu, setAcctMenu] = useState(false);

  const loadAccounts = useCallback(() => {
    api.get("/email-accounts").then(({ data }) => setAccounts(data || [])).catch(() => {});
  }, []);
  useEffect(() => { loadAccounts(); }, [loadAccounts]);

  const syncAllAccounts = useCallback(async () => {
    setSyncingAll(true);
    try {
      const { data } = await api.get("/email-accounts");
      const active = (data || []).filter((a) => a.active);
      if (!active.length) { toast.error("Aucune boîte connectée. Ajoutez-en une via « Comptes email »."); setShowAccounts(true); return; }
      let total = 0;
      for (const a of active) {
        try { const { data: r } = await api.post(`/email-accounts/${a.id}/sync`); total += r.imported || 0; } catch { /* skip */ }
      }
      toast.success(`${total} nouvel(s) email(s) importé(s)`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSyncingAll(false); }
    // eslint-disable-next-line
  }, []);

  const startRailDrag = useCallback((e) => {
    e.preventDefault();
    draggingRef.current = true;
    const railLeft = railRef.current ? railRef.current.getBoundingClientRect().left : 0;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    const onMove = (ev) => {
      if (!draggingRef.current) return;
      const x = ev.touches ? ev.touches[0].clientX : ev.clientX;
      const w = Math.max(160, Math.min(400, x - railLeft));
      setRailWidth(w);
    };
    const onUp = () => {
      draggingRef.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      setRailWidth((w) => { localStorage.setItem("fd_mail_rail_w", String(w)); return w; });
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove);
    window.addEventListener("touchend", onUp);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const cq = selectedId && selectedId !== "all" ? `&company_id=${selectedId}` : "";
      let url;
      if (folder.startsWith("contact:")) {
        url = `/emails?folder=contact&contact=${encodeURIComponent(folder.slice(8))}`;
      } else {
        url = `/emails?folder=${folder}${cq}`;
      }
      const { data } = await api.get(url);
      setEmails(data);
    } finally { setLoading(false); }
  }, [folder, selectedId]);

  const loadFolders = useCallback(async () => {
    const cq = selectedId && selectedId !== "all" ? `?company_id=${selectedId}` : "";
    try { const { data } = await api.get(`/emails/folders${cq}`); setSmartFolders(data); } catch { setSmartFolders([]); }
    try { const { data } = await api.get(`/emails/client-folders`); setClientFolders(data); } catch { setClientFolders([]); }
    try { const { data } = await api.get(`/emails/rules`); setRules(data); } catch { setRules([]); }
    try { const { data } = await api.get(`/emails/manual-folders`); setManualFolders(data); } catch { setManualFolders([]); }
  }, [selectedId]);

  const saveMf = async () => {
    const payload = { name: mfDialog.name, color: mfDialog.color };
    try {
      if (mfDialog.id) await api.patch(`/emails/manual-folders/${mfDialog.id}`, payload);
      else await api.post(`/emails/manual-folders`, payload);
      setMfDialog(null); loadFolders(); toast.success("Dossier enregistré");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const deleteMf = async (id) => {
    if (!window.confirm("Supprimer ce dossier ? Les emails ne seront pas supprimés.")) return;
    try { await api.delete(`/emails/manual-folders/${id}`); if (folder === `mf:${id}`) setFolder("inbox"); setMfDialog(null); loadFolders(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const toggleSelect = (id) => setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const clearSelect = () => { setSelectMode(false); setSelectedIds([]); };
  const moveSelected = async (folderId) => {
    if (!selectedIds.length) return;
    try {
      await api.post(`/emails/move`, { email_ids: selectedIds, folder_id: folderId || null });
      const dest = folderId ? (manualFolders.find((f) => f.id === folderId)?.name || "le dossier") : "la réception";
      toast.success(`${selectedIds.length} email(s) déplacé(s) vers ${dest}`);
      clearSelect(); load(); loadFolders();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const markProcessed = async (processed) => {
    if (!selectedIds.length) return;
    try {
      const { data } = await api.post(`/emails/process`, { email_ids: selectedIds, processed });
      toast.success(processed ? `${data.updated} email(s) marqué(s) comme traité(s)` : `${data.updated} email(s) remis en réception`);
      clearSelect(); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const saveRule = async () => {
    const payload = { name: ruleDialog.name, color: ruleDialog.color, keywords: (ruleDialog.keywords || "").split(",").map((k) => k.trim()).filter(Boolean) };
    try {
      if (ruleDialog.id) await api.patch(`/emails/rules/${ruleDialog.id}`, payload);
      else await api.post(`/emails/rules`, payload);
      setRuleDialog(null); loadFolders(); toast.success("Dossier automatique enregistré");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const deleteRule = async (id) => {
    if (!window.confirm("Supprimer ce dossier automatique ?")) return;
    try { await api.delete(`/emails/rules/${id}`); if (folder === `rule:${id}`) setFolder("inbox"); setRuleDialog(null); loadFolders(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const runAutoDetect = async () => {
    setAiOpen(true); setAiLoading(true); setAiSugg([]);
    try { const { data } = await api.post(`/emails/rules/auto-detect`); setAiSugg(data.suggestions || []); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setAiOpen(false); }
    finally { setAiLoading(false); }
  };
  const acceptSugg = async (s) => {
    try { await api.post(`/emails/rules`, { name: s.name, keywords: s.keywords, color: "#0EA5E9" }); setAiSugg((arr) => arr.filter((x) => x !== s)); loadFolders(); toast.success(`Dossier « ${s.name} » créé`); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const saveFolder = async () => {
    const payload = { name: folderDialog.name, color: folderDialog.color, emails: folderDialog.emails };
    try {
      if (folderDialog.id) await api.patch(`/emails/client-folders/${folderDialog.id}`, payload);
      else await api.post(`/emails/client-folders`, payload);
      setFolderDialog(null); loadFolders(); toast.success("Dossier client enregistré");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const deleteFolder = async (id) => {
    if (!window.confirm("Supprimer ce dossier client ?")) return;
    try { await api.delete(`/emails/client-folders/${id}`); if (folder === `cf:${id}`) setFolder("inbox"); setFolderDialog(null); loadFolders(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  useEffect(() => { load(); }, [load, events]);
  useEffect(() => { loadFolders(); }, [loadFolders, events]);
  useEffect(() => { setRailOpen(false); }, [folder]);

  const convertCid = convert?.company_id;
  const ticketCid = ticketDraft?.company_id;
  const activeCid = convertCid || ticketCid || "";
  useEffect(() => {
    if (!activeCid) { setClientOpts([]); return; }
    api.get("/clients", { params: { company_id: activeCid } })
      .then((r) => setClientOpts((r.data || []).filter((c) => c.email)))
      .catch(() => setClientOpts([]));
  }, [activeCid]);
  useEffect(() => { api.get("/users").then((r) => setUsers(r.data)); }, []);
  const [contactRecipients, setContactRecipients] = useState([]);
  useEffect(() => {
    api.get("/contacts").then((r) => setContactRecipients((r.data || []).filter((c) => c.email).map((c) => ({ id: "ct_" + c.id, email: c.email, name: c.name })))).catch(() => {});
  }, [events]);
  const recipients = React.useMemo(() => {
    const seen = new Set(); const out = [];
    [...users, ...contactRecipients].forEach((u) => { const k = (u.email || "").toLowerCase(); if (k && !seen.has(k)) { seen.add(k); out.push(u); } });
    return out;
  }, [users, contactRecipients]);

  const open = async (e) => {
    const { data } = await api.get(`/emails/${e.id}`);
    setSelected(data);
    setEmails((prev) => prev.map((x) => (x.id === e.id ? { ...x, is_read: true } : x)));
    refreshSummary();
    setAiSummary({ count: 0, points: [], loading: true, open: true });
    api.post(`/emails/${e.id}/summary`)
      .then(({ data: s }) => setAiSummary({ count: s.count || 0, points: s.summary || [], loading: false, open: true }))
      .catch(() => setAiSummary({ count: 0, points: [], loading: false, open: true }));
  };

  const toggleStar = async (e) => {
    await api.patch(`/emails/${e.id}`, { starred: !e.starred });
    load();
  };

  const createTaskFromEmail = async () => {
    const cid = convert.company_id || managed[0]?.id;
    if (!cid) { toast.error("Sélectionnez une entreprise"); return; }
    try {
      await api.post("/tasks", {
        company_id: cid, title: convert.title, description: convert.description, brief: convert.brief || "",
        client_email: convert.client_email, source_email_id: convert.source_email_id,
        priority: convert.priority, assigned_to: convert.assigned_to || null, deadline: convert.deadline || null,
      });
      toast.success("Tâche créée", { description: "Retrouvez-la dans l'onglet Tâches" });
      setConvert(null);
      navigate("/tasks");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const createTicketFromEmail = async () => {
    const cid = ticketDraft.company_id || managed[0]?.id;
    if (!cid) { toast.error("Sélectionnez une entreprise"); return; }
    try {
      await api.post("/tickets", { company_id: cid, subject: ticketDraft.subject, body: ticketDraft.body, client_email: ticketDraft.client_email, source: "email", assigned_to: ticketDraft.assigned_to || null, priority: ticketDraft.priority || "medium", deadline: ticketDraft.deadline || null });
      toast.success("Ticket créé", { description: "Retrouvez-le dans l'onglet Tickets" });
      setTicketDraft(null);
      navigate("/tickets");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const replyTo = (e) => setCompose({ to: e.from_email, subject: `Re: ${e.subject}`, body: `\n\n--- Message original ---\n${e.body}`, body_html: `<br><br><blockquote style="border-left:2px solid rgba(255,255,255,0.15);padding-left:12px;color:#a1a1aa;">${e.body_html || (e.body || "").replace(/\n/g, "<br>")}</blockquote>`, in_reply_to: e.id, company_id: e.company_id || null });
  const forward = (e) => setCompose({ to: "", subject: `Tr: ${e.subject}`, body: `\n\n--- Message transféré ---\nDe : ${e.from_name || e.from_email}\nObjet : ${e.subject}\n\n${e.body}`, body_html: `<br><br><div style="color:#a1a1aa;">--- Message transféré ---<br>De : ${e.from_name || e.from_email}<br>Objet : ${e.subject}</div><br>${e.body_html || (e.body || "").replace(/\n/g, "<br>")}`, in_reply_to: null });

  const EmailRow = ({ e }) => {
    const other = folder === "sent" ? e.to_email : e.from_name || e.from_email;
    const checked = selectedIds.includes(e.id);
    const startLongPress = () => {
      clearTimeout(longPressRef.current);
      longPressRef.current = setTimeout(() => { setSelectMode(true); toggleSelect(e.id); }, 450);
    };
    const cancelLongPress = () => clearTimeout(longPressRef.current);
    return (
      <div data-testid={`email-item-${e.id}`} onClick={() => (selectMode ? toggleSelect(e.id) : open(e))}
        onTouchStart={startLongPress} onTouchEnd={cancelLongPress} onTouchMove={cancelLongPress} onContextMenu={(ev) => ev.preventDefault()}
        style={{ WebkitTouchCallout: "none" }}
        className={`p-4 border-b border-white/[0.06] hover:bg-background cursor-pointer transition-colors select-none ${selected?.id === e.id ? "bg-background" : ""} ${checked ? "bg-klein/[0.12]" : ""} ${!e.is_read && folder === "inbox" && !checked ? "bg-klein/[0.07]" : ""}`}>
        <div className="flex items-center gap-3">
          {selectMode && (
            <input type="checkbox" data-testid={`email-check-${e.id}`} checked={checked} onChange={(ev) => { ev.stopPropagation(); toggleSelect(e.id); }} onClick={(ev) => ev.stopPropagation()} className="w-4 h-4 accent-klein shrink-0" />
          )}
          <Avatar name={typeof other === "string" ? other : ""} color="#27272A" size={36} />
          <div className="min-w-0 flex-1">
            <div className="flex justify-between items-baseline gap-2">
              <span className={`truncate text-sm ${!e.is_read && folder === "inbox" ? "font-bold" : "font-medium"}`}>{other}</span>
              <span className="text-[11px] text-zinc-400 font-mono-label shrink-0">{timeAgo(e.created_at)}</span>
            </div>
            <div className={`truncate text-sm ${!e.is_read && folder === "inbox" ? "font-semibold text-zinc-100" : "text-zinc-300"}`}>{e.subject}</div>
            <div className="truncate text-xs text-zinc-400">{e.body}</div>
          </div>
          {!e.is_read && folder === "inbox" && !selectMode && <span className="w-2 h-2 rounded-full bg-klein shrink-0" />}
        </div>
      </div>
    );
  };

  return (
    <div className="flex h-full fd-clean">
      {/* Mobile drawer backdrop */}
      {railOpen && <div data-testid="mail-rail-backdrop" onClick={() => setRailOpen(false)} className="fixed top-16 bottom-0 inset-x-0 bg-black/40 z-30 xl:hidden" />}
      {/* Folder rail (sidebar on desktop, drawer on mobile/tablet) */}
      <div ref={railRef} data-testid="mail-folder-rail" style={{ "--rail-w": railWidth + "px" }} className={`${railOpen ? "flex" : "hidden"} xl:flex flex-col w-72 xl:w-[var(--rail-w)] max-w-[85vw] border-r border-white/10 bg-card p-3 gap-1 fixed xl:relative top-16 bottom-0 left-0 z-40 xl:z-auto shadow-2xl xl:shadow-none overflow-y-auto`}>
        {/* Resize handle (desktop only) */}
        <div data-testid="mail-rail-resize" onMouseDown={startRailDrag} onTouchStart={startRailDrag}
          className="hidden xl:block absolute top-0 right-0 w-1.5 h-full cursor-col-resize hover:bg-klein/50 active:bg-klein transition-colors z-50" title="Glisser pour redimensionner" />
        <button data-testid="mail-rail-close" onClick={() => setRailOpen(false)} className="xl:hidden self-end p-1.5 -mt-1 mb-1 rounded-md hover:bg-white/[0.06] text-zinc-400"><X size={18} /></button>
        <div className="relative mb-3">
          <button data-testid="mail-active-account" onClick={() => (accounts.length ? setAcctMenu((o) => !o) : setShowAccounts(true))}
            className="w-full flex items-center gap-2 px-2.5 py-2 rounded-lg bg-background border border-white/10 hover:border-klein/40 text-left transition-colors">
            <div className="w-7 h-7 rounded-lg bg-klein/15 flex items-center justify-center shrink-0"><Mail size={14} className="text-klein" /></div>
            <div className="min-w-0 flex-1">
              <div className="text-[9px] uppercase tracking-wider text-zinc-500 leading-none mb-0.5">{accounts.length ? "Boîte active" : "Aucune boîte"}</div>
              <div className="text-xs font-medium truncate">{accounts.length ? (accounts.find((a) => a.active) || accounts[0]).email : "Connecter une boîte"}</div>
            </div>
            <ChevronDown size={13} className={`text-zinc-400 shrink-0 transition-transform ${acctMenu ? "rotate-180" : ""}`} />
          </button>
          {acctMenu && accounts.length > 0 && (
            <div data-testid="mail-account-menu" className="absolute left-0 right-0 top-full mt-1 z-40 bg-card border border-white/10 rounded-xl shadow-2xl p-1.5">
              {accounts.map((a) => (
                <div key={a.id} className="flex items-center gap-2 px-2 py-1.5 rounded-lg">
                  <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${a.status === "connected" ? "bg-emerald-400" : "bg-red-400"}`} />
                  <span className="text-xs truncate flex-1" title={a.email}>{a.email}</span>
                </div>
              ))}
              <button data-testid="mail-account-manage" onClick={() => { setAcctMenu(false); setShowAccounts(true); }} className="w-full mt-1 flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-xs text-klein hover:bg-klein/10"><Settings size={12} /> Gérer les comptes</button>
            </div>
          )}
        </div>
        <button data-testid="compose-button" onClick={() => setCompose({ to: "", subject: "", body: "", in_reply_to: null })}
          className="mb-3 bg-klein text-zinc-950 py-2.5 rounded-md font-medium text-sm flex items-center justify-center gap-2 hover:opacity-90 transition-colors">
          <PenSquare size={16} /> Nouveau
        </button>
        {FOLDERS.map((f) => {
          const Icon = f.icon;
          return (
            <button key={f.id} data-testid={`folder-${f.id}`} onClick={() => { setFolder(f.id); setSelected(null); }}
              className={`flex items-center gap-2.5 px-3 py-2 rounded-md text-sm font-medium transition-colors ${folder === f.id ? "bg-white/[0.06] text-zinc-100" : "text-zinc-500 hover:bg-background"}`}>
              <Icon size={16} /> {f.label}
            </button>
          );
        })}
        <button data-testid="open-attachments-hub" onClick={openAttachHub}
          className="flex items-center gap-2.5 px-3 py-2 rounded-md text-sm font-medium transition-colors text-zinc-500 hover:bg-background hover:text-klein">
          <Paperclip size={16} /> Pièces jointes
        </button>
        <button data-testid="open-email-accounts" onClick={() => setShowAccounts(true)}
          className="flex items-center gap-2.5 px-3 py-2 rounded-md text-sm font-medium transition-colors text-zinc-500 hover:bg-background hover:text-klein">
          <Mail size={16} /> Comptes email
        </button>
        {/* Managed client folders (group multiple addresses) */}
        <div className="mt-4 flex flex-col gap-0.5" data-testid="client-folders">
          <div className="px-3 mb-1 flex items-center justify-between font-mono-label text-[10px] uppercase tracking-widest text-zinc-500">
            <span className="flex items-center gap-1.5"><FolderTree size={12} /> Dossiers clients</span>
            <button data-testid="new-client-folder" onClick={() => setFolderDialog({ name: "", emails: [""], color: "#3B82F6" })} className="p-0.5 rounded hover:bg-white/[0.08] text-zinc-400 hover:text-klein"><PenSquare size={11} /></button>
          </div>
          {clientFolders.map((cf) => {
            const id = `cf:${cf.id}`;
            return (
              <div key={cf.id} className={`group flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${folder === id ? "bg-white/[0.06] text-zinc-100" : "text-zinc-400 hover:bg-background"}`}>
                <button data-testid={`folder-cf-${cf.id}`} onClick={() => { setFolder(id); setSelected(null); }} className="flex items-center gap-2 flex-1 min-w-0 text-left">
                  <span className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0" style={{ background: cf.color + "26", color: cf.color }}>{(cf.name || "?").slice(0, 1).toUpperCase()}</span>
                  <span className="flex-1 truncate">{cf.name}</span>
                </button>
                {cf.unread > 0 && <span className="text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center text-white" style={{ background: cf.color }}>{cf.unread}</span>}
                <button data-testid={`edit-cf-${cf.id}`} onClick={() => setFolderDialog({ ...cf, emails: cf.emails.length ? cf.emails : [""] })} className="opacity-0 group-hover:opacity-100 p-0.5 rounded text-zinc-500 hover:text-klein"><ChevronRight size={13} /></button>
              </div>
            );
          })}
          {clientFolders.length === 0 && <button data-testid="empty-create-folder" onClick={() => setFolderDialog({ name: "", emails: [""], color: "#3B82F6" })} className="px-3 py-1.5 text-[11px] text-zinc-500 hover:text-klein text-left">+ Créer un dossier client</button>}
        </div>

        {/* Rule-based auto folders */}
        <div className="mt-4 flex flex-col gap-0.5" data-testid="rule-folders">
          <div className="px-3 mb-1 flex items-center justify-between font-mono-label text-[10px] uppercase tracking-widest text-zinc-500">
            <span className="flex items-center gap-1.5"><Wand2 size={12} /> Dossiers auto</span>
            <div className="flex items-center gap-0.5">
              <button data-testid="ai-detect" onClick={runAutoDetect} title="Détecter avec l'IA" className="p-0.5 rounded hover:bg-white/[0.08] text-zinc-400 hover:text-klein"><Sparkles size={11} /></button>
              <button data-testid="new-rule" onClick={() => setRuleDialog({ name: "", keywords: "", color: "#F59E0B" })} className="p-0.5 rounded hover:bg-white/[0.08] text-zinc-400 hover:text-klein"><PenSquare size={11} /></button>
            </div>
          </div>
          {rules.map((r) => {
            const id = `rule:${r.id}`;
            return (
              <div key={r.id} className={`group flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${folder === id ? "bg-white/[0.06] text-zinc-100" : "text-zinc-400 hover:bg-background"}`}>
                <button data-testid={`folder-rule-${r.id}`} onClick={() => { setFolder(id); setSelected(null); }} className="flex items-center gap-2 flex-1 min-w-0 text-left">
                  <span className="w-6 h-6 rounded-md flex items-center justify-center shrink-0" style={{ background: r.color + "26", color: r.color }}><Wand2 size={12} /></span>
                  <span className="flex-1 truncate">{r.name}</span>
                </button>
                {r.unread > 0 && <span className="text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center text-white" style={{ background: r.color }}>{r.unread}</span>}
                <button data-testid={`edit-rule-${r.id}`} onClick={() => setRuleDialog({ ...r, keywords: (r.keywords || []).join(", ") })} className="opacity-0 group-hover:opacity-100 p-0.5 rounded text-zinc-500 hover:text-klein"><ChevronRight size={13} /></button>
              </div>
            );
          })}
          {rules.length === 0 && <button data-testid="empty-detect" onClick={runAutoDetect} className="px-3 py-1.5 text-[11px] text-klein hover:underline text-left flex items-center gap-1"><Sparkles size={11} /> Détecter automatiquement (IA)</button>}
        </div>

        {/* Manual folders (multi-select move) */}
        <div className="mt-4 flex flex-col gap-0.5" data-testid="manual-folders">
          <div className="px-3 mb-1 flex items-center justify-between font-mono-label text-[10px] uppercase tracking-widest text-zinc-500">
            <span className="flex items-center gap-1.5"><FolderInput size={12} /> Mes dossiers</span>
          </div>
          {manualFolders.map((mf) => {
            const id = `mf:${mf.id}`;
            return (
              <div key={mf.id} className={`group flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${folder === id ? "bg-white/[0.06] text-zinc-100" : "text-zinc-400 hover:bg-background"}`}>
                <button data-testid={`folder-mf-${mf.id}`} onClick={() => { setFolder(id); setSelected(null); }} className="flex items-center gap-2 flex-1 min-w-0 text-left">
                  <span className="w-6 h-6 rounded-md flex items-center justify-center shrink-0" style={{ background: mf.color + "26", color: mf.color }}><FolderTree size={12} /></span>
                  <span className="flex-1 truncate">{mf.name}</span>
                </button>
                {mf.count > 0 && <span className="text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center text-white" style={{ background: mf.color }}>{mf.count}</span>}
                <button data-testid={`edit-mf-${mf.id}`} onClick={() => setMfDialog({ ...mf })} className="opacity-0 group-hover:opacity-100 p-0.5 rounded text-zinc-500 hover:text-klein"><ChevronRight size={13} /></button>
              </div>
            );
          })}
          <button data-testid="new-manual-folder" onClick={() => setMfDialog({ name: "", color: "#6366F1" })} className="mt-1 flex items-center gap-1.5 px-3 py-2 rounded-md text-[12px] font-medium text-klein hover:bg-klein/[0.08] text-left border border-dashed border-klein/30"><FolderPlus size={14} /> Nouveau dossier</button>
        </div>

        {smartFolders.length > 0 && (
          <div className="mt-4 flex flex-col gap-0.5 min-h-0 overflow-y-auto" data-testid="smart-folders">
            <div className="px-3 mb-1 flex items-center gap-1.5 font-mono-label text-[10px] uppercase tracking-widest text-zinc-500"><FolderTree size={12} /> Autres expéditeurs</div>
            {smartFolders.map((sf) => {
              const id = `contact:${sf.email}`;
              return (
                <button key={sf.email} data-testid={`folder-client-${sf.email}`} onClick={() => { setFolder(id); setSelected(null); }}
                  className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${folder === id ? "bg-white/[0.06] text-zinc-100" : "text-zinc-400 hover:bg-background"}`}>
                  <span className="w-6 h-6 rounded-full bg-klein/15 text-klein flex items-center justify-center text-[10px] font-bold shrink-0">{(sf.name || sf.email).slice(0, 1).toUpperCase()}</span>
                  <span className="flex-1 truncate text-left">{sf.name || sf.email}</span>
                  {sf.unread > 0 && <span className="text-[10px] font-bold bg-klein text-zinc-950 rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center">{sf.unread}</span>}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* List pane */}
      <div className={`${selected ? "hidden md:flex" : "flex"} flex-col w-full md:w-80 lg:w-96 border-r border-white/10 bg-card`}>
        <div className="h-14 flex items-center justify-between px-4 border-b border-white/10 shrink-0">
          <div className="flex items-center gap-1 xl:hidden">
            <button data-testid="mail-open-folders" onClick={() => setRailOpen(true)} className="p-2 rounded-md text-klein hover:bg-white/[0.06] mr-1"><FolderTree size={18} /></button>
            {FOLDERS.map((f) => (
              <button key={f.id} data-testid={`folder-${f.id}-mobile`} onClick={() => { setFolder(f.id); setSelected(null); }}
                className={`px-2.5 py-1 rounded-md text-xs font-medium ${folder === f.id ? "bg-klein text-zinc-950" : "text-zinc-500"}`}>
                {f.label}
              </button>
            ))}
          </div>
          <span className="hidden xl:block font-cabinet font-bold text-lg">{folder.startsWith("cf:") ? (clientFolders.find((c) => `cf:${c.id}` === folder)?.name || "Dossier client") : folder.startsWith("mf:") ? (manualFolders.find((m) => `mf:${m.id}` === folder)?.name || "Dossier") : folder.startsWith("rule:") ? (rules.find((r) => `rule:${r.id}` === folder)?.name || "Dossier auto") : folder.startsWith("contact:") ? folder.slice(8) : FOLDERS.find((f) => f.id === folder)?.label}</span>
          <div className="flex items-center gap-1">
            <button data-testid="mail-sync-all" onClick={syncAllAccounts} disabled={syncingAll} title="Synchroniser les boîtes email"
              className="p-2 rounded-md text-zinc-400 hover:text-klein hover:bg-white/[0.06] disabled:opacity-50">
              <RefreshCw size={16} className={syncingAll ? "animate-spin" : ""} />
            </button>
            <button data-testid="compose-button-mobile" onClick={() => setCompose({ to: "", subject: "", body: "", in_reply_to: null })}
              className="xl:hidden p-2 rounded-md bg-klein text-zinc-950"><PenSquare size={16} /></button>
          </div>
        </div>
        <div className="h-9 flex items-center gap-2 px-3 border-b border-white/[0.06] shrink-0 text-xs">
          <select data-testid="mail-sort" value={sortMode} onChange={(e) => setSortMode(e.target.value)} className="fd-inline-select !h-7 !text-xs">
            <option value="recent">Plus récents</option>
            <option value="old">Plus anciens</option>
            <option value="unread">Non lus d'abord</option>
            <option value="sender">Expéditeur (A→Z)</option>
          </select>
          <div className="flex-1" />
          <button data-testid="mail-select-toggle" onClick={() => { setSelectMode((s) => !s); setSelectedIds([]); }}
            className={`flex items-center gap-1 px-2 h-7 rounded-md ${selectMode ? "bg-klein/15 text-klein" : "text-zinc-400 hover:text-klein hover:bg-white/[0.05]"}`}>
            <CheckSquare size={13} /> Sélection
          </button>
          <button data-testid="mail-group-toggle" onClick={() => setGroupMode((m) => (m === "date" ? (selectedId === "all" ? "company" : "none") : "date"))}
            className="flex items-center gap-1 px-2 h-7 rounded-md text-zinc-400 hover:text-klein hover:bg-white/[0.05]">
            <CalendarDays size={13} /> {groupMode === "date" ? "Par date" : groupMode === "company" ? "Par entreprise" : "Liste"}
          </button>
        </div>
        {selectMode && (
          <div data-testid="mail-bulk-bar" className="flex items-center gap-2 px-3 py-2 border-b border-white/[0.06] bg-klein/[0.06] shrink-0 text-xs flex-wrap">
            <span className="font-medium text-klein">{selectedIds.length} sélectionné(s)</span>
            <div className="flex-1" />
            {folder === "processed" ? (
              <button data-testid="mail-bulk-unprocess" onClick={() => markProcessed(false)} disabled={!selectedIds.length} className="flex items-center gap-1.5 px-3 h-7 rounded-md bg-zinc-500/15 text-zinc-200 font-medium disabled:opacity-50 hover:bg-zinc-500/25">
                <Undo2 size={13} /> Remettre en réception
              </button>
            ) : (
              <button data-testid="mail-bulk-process" onClick={() => markProcessed(true)} disabled={!selectedIds.length} className="flex items-center gap-1.5 px-3 h-7 rounded-md bg-emerald-500/15 text-emerald-400 font-medium disabled:opacity-50 hover:bg-emerald-500/25">
                <CheckCheck size={13} /> Marquer comme traité
              </button>
            )}
            <select data-testid="mail-bulk-folder" defaultValue="" onChange={(e) => { if (e.target.value) { moveSelected(e.target.value === "__none__" ? null : e.target.value); e.target.value = ""; } }} disabled={!selectedIds.length} className="fd-inline-select !h-7 !text-xs disabled:opacity-50">
              <option value="">Déplacer vers…</option>
              {manualFolders.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
              <option value="__none__">Réception (retirer)</option>
            </select>
            <button data-testid="mail-bulk-cancel" onClick={clearSelect} className="px-2 h-7 rounded-md text-zinc-400 hover:text-signal">Annuler</button>
          </div>
        )}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-8 flex justify-center text-zinc-400"><Loader2 className="animate-spin" /></div>
          ) : emails.length === 0 ? (
            <div className="p-12 text-center text-zinc-400 text-sm">Aucun message ici.</div>
          ) : groupMode === "date" ? (
            (() => {
              const buckets = bucketByDate(sortEmails(emails, sortMode));
              return buckets.map((b) => (
                <div key={b.key} data-testid={`mail-dategroup-${b.key}`}>
                  <div className="sticky top-0 z-[1] px-4 py-1.5 bg-card/95 backdrop-blur border-b border-white/[0.06] text-[11px] font-semibold uppercase tracking-wider text-zinc-500">{b.label} <span className="text-zinc-600">· {b.items.length}</span></div>
                  {b.items.map((e) => <EmailRow key={e.id} e={e} />)}
                </div>
              ));
            })()
          ) : (groupMode === "company" && selectedId === "all" && !folder.startsWith("contact:")) ? (
            (() => {
              const groups = [];
              const idx = {};
              sortEmails(emails, sortMode).forEach((e) => {
                const key = e.company_name || "__none__";
                if (idx[key] === undefined) { idx[key] = groups.length; groups.push({ key, name: e.company_name, items: [] }); }
                groups[idx[key]].items.push(e);
              });
              return groups.map((g) => {
                const isCollapsed = collapsedCo[g.key];
                return (
                  <div key={g.key} data-testid={`mail-group-${g.key}`}>
                    <button onClick={() => setCollapsedCo((p) => ({ ...p, [g.key]: !p[g.key] }))}
                      className="w-full sticky top-0 z-[1] flex items-center gap-2 px-4 py-2 bg-card/95 backdrop-blur border-b border-white/[0.06] text-left hover:bg-background transition-colors">
                      {isCollapsed ? <ChevronRight size={14} className="text-zinc-400" /> : <ChevronDown size={14} className="text-zinc-400" />}
                      <Building2 size={13} className="text-klein" />
                      <span className="text-xs font-semibold uppercase tracking-wide truncate flex-1">{g.name || "Sans entreprise"}</span>
                      <span className="text-[11px] text-zinc-500 tabular-nums">{g.items.length}</span>
                    </button>
                    {!isCollapsed && g.items.map((e) => <EmailRow key={e.id} e={e} />)}
                  </div>
                );
              });
            })()
          ) : (
            sortEmails(emails, sortMode).map((e) => <EmailRow key={e.id} e={e} />)
          )}
        </div>
      </div>

      {/* Detail pane */}
      <div className={`${selected ? "flex" : "hidden md:flex"} flex-1 flex-col bg-background min-w-0`}>
        {selected ? (
          <>
            <div className="h-14 border-b border-white/10 bg-card flex items-center justify-between px-4 shrink-0">
              <button data-testid="back-button" onClick={() => setSelected(null)} className="md:hidden p-2 rounded-md hover:bg-white/[0.06]"><ArrowLeft size={18} /></button>
              <div className="flex items-center gap-1 ml-auto">
                <button data-testid="star-button" onClick={() => toggleStar(selected)} className="p-2 rounded-md hover:bg-white/[0.06]">
                  <Star size={18} className={selected.starred ? "fill-amber-400 text-amber-400" : "text-zinc-400"} />
                </button>
                <button data-testid="reply-button" onClick={() => replyTo(selected)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-md hover:bg-white/[0.06] text-sm font-medium"><Reply size={16} /> Répondre</button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-6 lg:p-10">
              <div className="max-w-3xl">
              <h1 className="font-cabinet font-bold text-2xl lg:text-3xl tracking-tight mb-5 break-words">{selected.subject}</h1>
              {(aiSummary.loading || aiSummary.count >= 2) && (
                <div data-testid="ai-thread-summary" className="mb-6 rounded-xl border border-klein/25 bg-klein/[0.06] overflow-hidden">
                  <button onClick={() => setAiSummary((s) => ({ ...s, open: !s.open }))} className="w-full flex items-center gap-2 px-4 py-3 text-left">
                    <Wand2 size={15} className="text-klein shrink-0" />
                    <span className="font-cabinet font-bold text-sm">Aperçu IA</span>
                    {aiSummary.count >= 2 && <span className="text-[11px] text-zinc-400 font-mono-label">· {aiSummary.count} messages</span>}
                    {aiSummary.loading
                      ? <Loader2 size={15} className="ml-auto animate-spin text-klein" />
                      : <ChevronDown size={16} className={`ml-auto text-zinc-400 transition-transform ${aiSummary.open ? "" : "-rotate-90"}`} />}
                  </button>
                  {aiSummary.open && !aiSummary.loading && (
                    <div className="px-4 pb-3.5">
                      {aiSummary.points.length ? (
                        <>
                          <ul className="space-y-2">
                            {aiSummary.points.map((p, i) => (
                              <li key={i} data-testid={`ai-summary-point-${i}`} className="flex gap-2.5 text-sm leading-relaxed text-foreground">
                                <span className="mt-2 w-1.5 h-1.5 rounded-full bg-klein shrink-0" /><span>{p}</span>
                              </li>
                            ))}
                          </ul>
                          <p className="mt-3 text-[11px] text-zinc-500">Résumé généré par l'IA · des erreurs sont possibles.</p>
                        </>
                      ) : <p className="text-sm text-zinc-500">Résumé indisponible pour ce fil.</p>}
                    </div>
                  )}
                </div>
              )}
              <div className="flex items-start gap-3 mb-5">
                <Avatar name={selected.from_name || selected.from_email} color="#27272A" size={44} src={selected.from_avatar} />
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-foreground">{selected.from_name || selected.from_email}</span>
                    {(!!selected.from_user_id || users.some((u) => (u.email || "").toLowerCase() === (selected.from_email || "").toLowerCase()))
                      ? <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold text-emerald-600 bg-emerald-500/15"><BadgeCheck size={11} /> Contact connu</span>
                      : <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold text-amber-600 bg-amber-500/15"><UserPlus size={11} /> Nouveau contact</span>}
                  </div>
                  <div className="text-sm text-zinc-500 font-mono-label mt-0.5 truncate">{selected.from_email} <span className="text-zinc-400">→</span> {selected.to_email}</div>
                </div>
                <div className="ml-auto text-right shrink-0">
                  <div className="text-xs font-medium text-zinc-500">{new Date(selected.created_at).toLocaleDateString("fr-FR", { weekday: "short", day: "2-digit", month: "short", year: "numeric" })}</div>
                  <div className="text-[11px] text-zinc-400 font-mono-label">reçu à {new Date(selected.created_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</div>
                </div>
              </div>
              <div className="bg-card border border-white/10 rounded-xl p-5 lg:p-6 shadow-[0_1px_2px_rgba(16,24,40,0.06),0_8px_24px_-12px_rgba(16,24,40,0.12)]">
                {selected.cc && <div className="text-xs text-zinc-500 mb-3 pb-3 border-b border-white/10">Cc : {selected.cc}</div>}
                {selected.body_html
                  ? <div className="fd-mail-body text-foreground leading-relaxed" dangerouslySetInnerHTML={{ __html: selected.body_html }} />
                  : <div className="whitespace-pre-wrap text-foreground leading-relaxed">{selected.body}</div>}
              </div>

              {(selected.attachments || []).length > 0 && (
                <div className="mt-5" data-testid="email-attachments">
                  <p className="font-mono-label text-[10px] uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1.5"><Paperclip size={12} /> {selected.attachments.length} pièce(s) jointe(s)</p>
                  <div className="flex flex-wrap gap-2">
                    {selected.attachments.map((a) => (
                      <a key={a.id} data-testid={`email-att-${a.id}`} href={`${process.env.REACT_APP_BACKEND_URL}${a.url}`} target="_blank" rel="noreferrer" download={a.name}
                        className="group flex items-center gap-2.5 pl-2.5 pr-3 py-2 rounded-xl bg-card border border-white/10 hover:border-klein/40 transition-colors max-w-[260px]">
                        <span className="w-8 h-8 rounded-lg bg-klein/12 text-klein flex items-center justify-center shrink-0"><FileIcon size={15} /></span>
                        <span className="min-w-0">
                          <span className="block text-sm truncate" title={a.name}>{a.name}</span>
                          <span className="block text-[11px] text-zinc-500">{fmtSize(a.size)}</span>
                        </span>
                        <Download size={14} className="text-zinc-400 group-hover:text-klein shrink-0 ml-1" />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Action bar below the email */}
              <div data-testid="email-action-bar" className="mt-7 pt-6 border-t border-white/10">
                <p className="font-mono-label text-[10px] uppercase tracking-widest text-zinc-500 mb-3">Traiter ce message</p>
                <div className="flex flex-wrap gap-2">
                  <button data-testid="action-reply" onClick={() => replyTo(selected)} className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-klein text-zinc-950 text-sm font-semibold hover:-translate-y-px hover:shadow-[0_4px_14px_0_rgba(37,99,235,0.39)] transition-all"><Reply size={16} /> Répondre</button>
                  <button data-testid="action-forward" onClick={() => forward(selected)} className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-card border border-black/10 text-sm font-medium hover:bg-white/[0.06] hover:border-klein/40 transition-colors shadow-sm"><Forward size={16} className="text-zinc-400" /> Transférer</button>
                  <button data-testid="action-to-task" onClick={() => setConvert({ title: selected.subject, description: selected.body, brief: "", client_email: selected.from_email, source_email_id: selected.id, priority: "medium", company_id: (selectedId && selectedId !== "all" ? selectedId : managed[0]?.id) || "", assigned_to: "", deadline: "" })} className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-card border border-black/10 text-sm font-medium hover:bg-white/[0.06] hover:border-emerald-500/40 transition-colors shadow-sm"><ListTodo size={16} className="text-emerald-500" /> Créer en tâche</button>
                  <button data-testid="action-to-ticket" onClick={() => setTicketDraft({ company_id: (selectedId && selectedId !== "all" ? selectedId : managed[0]?.id) || "", subject: selected.subject, body: selected.body, client_email: selected.from_email, assigned_to: "", priority: "medium", deadline: "" })} className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-card border border-black/10 text-sm font-medium hover:bg-white/[0.06] hover:border-violet-500/40 transition-colors shadow-sm"><Ticket size={16} className="text-violet-500" /> Créer en ticket</button>
                </div>
              </div>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-zinc-300">
            <Inbox size={64} strokeWidth={1} />
            <p className="mt-4 text-sm text-zinc-400">Sélectionnez un message pour le lire</p>
          </div>
        )}
      </div>

      {/* Compose window */}
      {compose && (
        <EmailComposer
          initial={compose}
          users={recipients}
          onClose={() => setCompose(null)}
          onSent={() => { if (folder === "sent") load(); refreshSummary(); }}
        />
      )}

      {showAccounts && (
        <EmailAccountsModal onClose={() => setShowAccounts(false)} onChanged={() => { load(); loadAccounts(); }} />
      )}

      {/* Attachments hub dialog */}
      <Dialog open={!!attachHub} onOpenChange={(o) => !o && setAttachHub(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader><DialogTitle className="font-cabinet flex items-center gap-2"><Paperclip size={18} className="text-klein" /> Pièces jointes — {FOLDERS.find((f) => f.id === folder)?.label || "Dossier"}</DialogTitle></DialogHeader>
          <div className="overflow-y-auto -mx-1 px-1" data-testid="attachments-hub">
            {attachHub?.loading ? (
              <div className="py-12 flex justify-center text-zinc-400"><Loader2 className="animate-spin" /></div>
            ) : (attachHub?.items || []).length === 0 ? (
              <div className="py-12 text-center text-zinc-400"><Paperclip size={40} strokeWidth={1} className="mx-auto mb-3" /><p className="text-sm">Aucune pièce jointe dans ce dossier.</p></div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {attachHub.items.map((a, i) => (
                  <a key={i} data-testid={`hub-att-${i}`} href={`${process.env.REACT_APP_BACKEND_URL}${a.url}`} target="_blank" rel="noreferrer" download={a.name}
                    className="group flex items-center gap-3 p-3 rounded-xl bg-card border border-white/10 hover:border-klein/40 transition-colors">
                    <span className="w-10 h-10 rounded-lg bg-klein/12 text-klein flex items-center justify-center shrink-0"><FileIcon size={17} /></span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium truncate" title={a.name}>{a.name}</span>
                      <span className="block text-[11px] text-zinc-500 truncate">{a.from_name || a.from_email} · {fmtSize(a.size)}</span>
                      <span className="block text-[11px] text-zinc-600 truncate">{a.subject}</span>
                    </span>
                    <Download size={15} className="text-zinc-400 group-hover:text-klein shrink-0" />
                  </a>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>


      {/* Client folder create/edit dialog */}
      <Dialog open={!!folderDialog} onOpenChange={(o) => !o && setFolderDialog(null)}>        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-cabinet flex items-center gap-2"><FolderTree size={18} className="text-klein" /> {folderDialog?.id ? "Modifier le dossier client" : "Nouveau dossier client"}</DialogTitle></DialogHeader>
          {folderDialog && (
            <div className="space-y-3">
              <div>
                <label className="text-[11px] font-medium text-zinc-400 mb-1 block">Nom du client / dossier</label>
                <input data-testid="cf-name" value={folderDialog.name} onChange={(e) => setFolderDialog({ ...folderDialog, name: e.target.value })} placeholder="ex : Garage MRG" className="fd-input" />
              </div>
              <div>
                <label className="text-[11px] font-medium text-zinc-400 mb-1 block">Couleur</label>
                <div className="flex gap-2">
                  {["#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#06B6D4", "#EC4899"].map((c) => (
                    <button key={c} data-testid={`cf-color-${c}`} onClick={() => setFolderDialog({ ...folderDialog, color: c })} className={`w-7 h-7 rounded-full transition-transform ${folderDialog.color === c ? "ring-2 ring-offset-2 ring-offset-card scale-110" : ""}`} style={{ background: c, boxShadow: folderDialog.color === c ? `0 0 0 2px ${c}` : "none" }} />
                  ))}
                </div>
              </div>
              <div>
                <label className="text-[11px] font-medium text-zinc-400 mb-1 block">Adresses email du client</label>
                <p className="text-[11px] text-zinc-500 mb-2">Tous les emails reçus depuis ces adresses se classeront automatiquement dans ce dossier.</p>
                <div className="space-y-2">
                  {folderDialog.emails.map((addr, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <input data-testid={`cf-email-${i}`} value={addr} onChange={(e) => { const arr = [...folderDialog.emails]; arr[i] = e.target.value; setFolderDialog({ ...folderDialog, emails: arr }); }} placeholder="contact@client.com" className="fd-input flex-1" />
                      {folderDialog.emails.length > 1 && <button onClick={() => setFolderDialog({ ...folderDialog, emails: folderDialog.emails.filter((_, j) => j !== i) })} className="p-2 text-zinc-500 hover:text-signal"><ArrowLeft size={14} className="rotate-45" /></button>}
                    </div>
                  ))}
                </div>
                <button data-testid="cf-add-email" onClick={() => setFolderDialog({ ...folderDialog, emails: [...folderDialog.emails, ""] })} className="mt-2 text-xs text-klein hover:underline flex items-center gap-1"><UserPlus size={12} /> Ajouter une adresse</button>
              </div>
            </div>
          )}
          <DialogFooter className="flex items-center justify-between">
            {folderDialog?.id ? <button data-testid="cf-delete" onClick={() => deleteFolder(folderDialog.id)} className="h-9 px-3 rounded-lg text-sm text-signal hover:bg-signal/10 mr-auto">Supprimer</button> : <span className="mr-auto" />}
            <button onClick={() => setFolderDialog(null)} className="h-9 px-4 rounded-lg border border-white/10 text-sm">Annuler</button>
            <button data-testid="cf-save" onClick={saveFolder} disabled={!folderDialog?.name?.trim()} className="h-9 px-4 rounded-lg bg-klein text-white text-sm font-semibold disabled:opacity-60">Enregistrer</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rule (auto folder) dialog */}
      <Dialog open={!!ruleDialog} onOpenChange={(o) => !o && setRuleDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-cabinet flex items-center gap-2"><Wand2 size={18} className="text-klein" /> {ruleDialog?.id ? "Modifier le dossier auto" : "Nouveau dossier automatique"}</DialogTitle></DialogHeader>
          {ruleDialog && (
            <div className="space-y-3">
              <div><label className="text-[11px] font-medium text-zinc-400 mb-1 block">Nom du dossier</label><input data-testid="rule-name" value={ruleDialog.name} onChange={(e) => setRuleDialog({ ...ruleDialog, name: e.target.value })} placeholder="ex : Factures fournisseurs" className="fd-input" /></div>
              <div><label className="text-[11px] font-medium text-zinc-400 mb-1 block">Couleur</label><div className="flex gap-2">{["#F59E0B", "#10B981", "#3B82F6", "#EF4444", "#8B5CF6", "#06B6D4", "#EC4899"].map((c) => <button key={c} data-testid={`rule-color-${c}`} onClick={() => setRuleDialog({ ...ruleDialog, color: c })} className={`w-7 h-7 rounded-full ${ruleDialog.color === c ? "ring-2 ring-offset-2 ring-offset-card scale-110" : ""}`} style={{ background: c, boxShadow: ruleDialog.color === c ? `0 0 0 2px ${c}` : "none" }} />)}</div></div>
              <div>
                <label className="text-[11px] font-medium text-zinc-400 mb-1 block">Mots-clés (séparés par des virgules)</label>
                <p className="text-[11px] text-zinc-500 mb-1.5">Un email est classé ici si son objet ou son corps contient l'un de ces mots.</p>
                <textarea data-testid="rule-keywords" value={ruleDialog.keywords} onChange={(e) => setRuleDialog({ ...ruleDialog, keywords: e.target.value })} rows={2} placeholder="facture, invoice, devis" className="fd-input resize-none" />
              </div>
            </div>
          )}
          <DialogFooter className="flex items-center justify-between">
            {ruleDialog?.id ? <button data-testid="rule-delete" onClick={() => deleteRule(ruleDialog.id)} className="h-9 px-3 rounded-lg text-sm text-signal hover:bg-signal/10 mr-auto">Supprimer</button> : <span className="mr-auto" />}
            <button onClick={() => setRuleDialog(null)} className="h-9 px-4 rounded-lg border border-white/10 text-sm">Annuler</button>
            <button data-testid="rule-save" onClick={saveRule} disabled={!ruleDialog?.name?.trim()} className="h-9 px-4 rounded-lg bg-klein text-white text-sm font-semibold disabled:opacity-60">Enregistrer</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI auto-detect dialog */}
      <Dialog open={aiOpen} onOpenChange={(o) => !o && setAiOpen(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-cabinet flex items-center gap-2"><Sparkles size={18} className="text-klein" /> Dossiers suggérés par l'IA</DialogTitle></DialogHeader>
          {aiLoading ? <div className="py-10 flex flex-col items-center gap-3 text-zinc-400"><Loader2 className="animate-spin" /><span className="text-sm">Analyse de vos emails récents…</span></div>
            : aiSugg.length === 0 ? <div className="py-8 text-center text-zinc-500 text-sm">Aucune suggestion pour le moment.</div>
            : (
              <div className="space-y-2" data-testid="ai-suggestions">
                {aiSugg.map((s, i) => (
                  <div key={i} className="rounded-xl border border-white/10 bg-card p-3 flex items-start gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold flex items-center gap-2">{s.name} <span className="text-[10px] text-zinc-500">· {s.match_count} email(s)</span></div>
                      <div className="flex flex-wrap gap-1 mt-1.5">{s.keywords.map((k, j) => <span key={j} className="text-[10px] px-1.5 py-0.5 rounded bg-white/[0.06] text-zinc-400">{k}</span>)}</div>
                    </div>
                    <button data-testid={`accept-sugg-${i}`} onClick={() => acceptSugg(s)} className="shrink-0 h-8 px-3 rounded-full bg-klein/15 text-klein text-xs font-semibold hover:bg-klein/25">Créer</button>
                  </div>
                ))}
              </div>
            )}
        </DialogContent>
      </Dialog>


      {/* Manual folder create/edit dialog */}
      <Dialog open={!!mfDialog} onOpenChange={(o) => !o && setMfDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-cabinet flex items-center gap-2"><FolderTree size={18} className="text-klein" /> {mfDialog?.id ? "Modifier le dossier" : "Nouveau dossier"}</DialogTitle></DialogHeader>
          {mfDialog && (
            <div className="space-y-4">
              <div><label className="text-[11px] font-medium text-zinc-400 mb-1 block">Nom du dossier</label><input data-testid="mf-name" value={mfDialog.name} onChange={(e) => setMfDialog({ ...mfDialog, name: e.target.value })} placeholder="ex : À traiter, Urgent, Archives 2026" className="fd-input" autoFocus /></div>
              <div><label className="text-[11px] font-medium text-zinc-400 mb-1 block">Couleur</label><div className="flex gap-2 flex-wrap">{["#6366F1", "#F59E0B", "#10B981", "#3B82F6", "#EF4444", "#8B5CF6", "#06B6D4", "#EC4899"].map((c) => <button key={c} data-testid={`mf-color-${c}`} onClick={() => setMfDialog({ ...mfDialog, color: c })} className={`w-7 h-7 rounded-full ${mfDialog.color === c ? "ring-2 ring-offset-2 ring-offset-card scale-110" : ""}`} style={{ background: c, boxShadow: mfDialog.color === c ? `0 0 0 2px ${c}` : "none" }} />)}</div></div>
              <p className="text-[11px] text-zinc-500">Astuce : activez « Sélection » dans la liste pour cocher plusieurs emails et les déplacer dans ce dossier.</p>
            </div>
          )}
          <DialogFooter className="flex items-center justify-between">
            {mfDialog?.id ? <button data-testid="mf-delete" onClick={() => deleteMf(mfDialog.id)} className="h-9 px-3 rounded-lg text-sm text-signal hover:bg-signal/10 mr-auto">Supprimer</button> : <span className="mr-auto" />}
            <button onClick={() => setMfDialog(null)} className="h-9 px-4 rounded-lg border border-white/10 text-sm">Annuler</button>
            <button data-testid="mf-save" onClick={saveMf} disabled={!mfDialog?.name?.trim()} className="h-9 px-4 rounded-lg bg-klein text-white text-sm font-semibold disabled:opacity-60">Enregistrer</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Convert to task dialog */}      <Dialog open={!!convert} onOpenChange={(o) => !o && setConvert(null)}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-cabinet flex items-center gap-2"><ListTodo size={20} className="text-emerald-500" /> Convertir en tâche</DialogTitle></DialogHeader>
          {convert && (
            <div className="space-y-3">
              <div>
                <label className="font-mono-label text-[11px] uppercase tracking-widest text-zinc-400">Titre</label>
                <input data-testid="task-title-input" value={convert.title} onChange={(e) => setConvert({ ...convert, title: e.target.value })}
                  className="w-full border border-white/10 rounded-md px-3 py-2.5 mt-1 bg-background focus:outline-none focus:ring-2 focus:ring-klein/20 focus:border-klein" />
              </div>
              <div>
                <label className="font-mono-label text-[11px] uppercase tracking-widest text-zinc-400">Brief (consignes à l'équipe)</label>
                <textarea data-testid="task-brief-input" value={convert.brief || ""} rows={3} placeholder="Ce qu'il faut faire, attentes, contexte…" onChange={(e) => setConvert({ ...convert, brief: e.target.value })}
                  className="w-full border border-white/10 rounded-md px-3 py-2.5 mt-1 bg-background focus:outline-none focus:ring-2 focus:ring-klein/20 focus:border-klein resize-none" />
              </div>
              <div>
                <label className="font-mono-label text-[11px] uppercase tracking-widest text-zinc-400">Contenu de l'email</label>
                <textarea value={convert.description} rows={4} onChange={(e) => setConvert({ ...convert, description: e.target.value })}
                  className="w-full border border-white/10 rounded-md px-3 py-2.5 mt-1 bg-background text-sm text-zinc-400 focus:outline-none focus:ring-2 focus:ring-klein/20 focus:border-klein resize-none" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-mono-label text-[11px] uppercase tracking-widest text-zinc-400">Entreprise</label>
                  <select data-testid="task-company-select" value={convert.company_id || ""} onChange={(e) => setConvert({ ...convert, company_id: e.target.value })} className="w-full border border-white/10 rounded-md px-3 py-2.5 mt-1 bg-background text-sm focus:outline-none focus:ring-2 focus:ring-klein/20 focus:border-klein">
                    <option value="">— Choisir —</option>
                    {managed.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="font-mono-label text-[11px] uppercase tracking-widest text-zinc-400">Assigné à</label>
                  <select data-testid="task-assignee-select" value={convert.assigned_to || ""} onChange={(e) => setConvert({ ...convert, assigned_to: e.target.value })} className="w-full border border-white/10 rounded-md px-3 py-2.5 mt-1 bg-background text-sm focus:outline-none focus:ring-2 focus:ring-klein/20 focus:border-klein">
                    <option value="">— Personne —</option>
                    {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="font-mono-label text-[11px] uppercase tracking-widest text-zinc-400">Priorité</label>
                  <select value={convert.priority} onChange={(e) => setConvert({ ...convert, priority: e.target.value })}
                    className="w-full border border-white/10 rounded-md px-3 py-2.5 mt-1 bg-background text-sm focus:outline-none focus:ring-2 focus:ring-klein/20 focus:border-klein">
                    <option value="low">Basse</option><option value="medium">Moyenne</option><option value="high">Haute</option>
                  </select>
                </div>
                <div>
                  <label className="font-mono-label text-[11px] uppercase tracking-widest text-zinc-400">Échéance</label>
                  <input data-testid="task-deadline-input" type="date" value={(convert.deadline || "").slice(0, 10)} onChange={(e) => setConvert({ ...convert, deadline: e.target.value })}
                    className="w-full border border-white/10 rounded-md px-3 py-2.5 mt-1 bg-background text-sm focus:outline-none focus:ring-2 focus:ring-klein/20 focus:border-klein" />
                </div>
                <div>
                  <label className="font-mono-label text-[11px] uppercase tracking-widest text-zinc-400">Client</label>
                  <select data-testid="task-client-select" value={(convert.client_email && clientOpts.some((c) => c.email === convert.client_email)) ? convert.client_email : (convert.client_email ? "__manual__" : "")} onChange={(e) => { if (e.target.value !== "__manual__") setConvert({ ...convert, client_email: e.target.value }); }} disabled={!convert.company_id} className="w-full border border-white/10 rounded-md px-3 py-2.5 mt-1 bg-background text-sm focus:outline-none focus:ring-2 focus:ring-klein/20 focus:border-klein disabled:opacity-50">
                    <option value="">{convert.company_id ? "— Choisir —" : "Choisir une entreprise"}</option>
                    {clientOpts.map((c) => <option key={c.email} value={c.email}>{c.name ? `${c.name} · ${c.email}` : c.email}</option>)}
                    {convert.client_email && !clientOpts.some((c) => c.email === convert.client_email) && <option value="__manual__">{`\u270F\uFE0F ${convert.client_email}`}</option>}
                  </select>
                </div>
              </div>
              <div>
                <label className="font-mono-label text-[11px] uppercase tracking-widest text-zinc-400">Autre adresse client (optionnel)</label>
                <input data-testid="task-client-manual" value={convert.client_email || ""} onChange={(e) => setConvert({ ...convert, client_email: e.target.value })} placeholder="saisir une adresse non listée" className="w-full border border-white/10 rounded-md px-3 py-2.5 mt-1 bg-background text-sm focus:outline-none focus:ring-2 focus:ring-klein/20 focus:border-klein" />
              </div>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setConvert(null)} className="px-4 py-2 rounded-md border border-white/10 text-sm font-medium">Annuler</button>
            <button data-testid="create-task-button" onClick={createTaskFromEmail} className="px-4 py-2 rounded-md bg-emerald-500 text-white text-sm font-semibold hover:brightness-110">Créer la tâche</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Convert to ticket dialog */}
      <Dialog open={!!ticketDraft} onOpenChange={(o) => !o && setTicketDraft(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-cabinet flex items-center gap-2"><Ticket size={20} className="text-klein" /> Créer un ticket</DialogTitle></DialogHeader>
          {ticketDraft && (
            <div className="space-y-3">
              <div>
                <label className="font-mono-label text-[11px] uppercase tracking-widest text-zinc-400">Entreprise</label>
                <select data-testid="ticket-company-select" value={ticketDraft.company_id} onChange={(e) => setTicketDraft({ ...ticketDraft, company_id: e.target.value })} className="w-full border border-white/10 rounded-md px-3 py-2.5 mt-1 text-sm focus:outline-none focus:ring-2 focus:ring-klein/20 focus:border-klein">
                  <option value="">— Choisir —</option>
                  {managed.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="font-mono-label text-[11px] uppercase tracking-widest text-zinc-400">Objet</label>
                <input data-testid="ticket-subject-input" value={ticketDraft.subject} onChange={(e) => setTicketDraft({ ...ticketDraft, subject: e.target.value })} className="w-full border border-white/10 rounded-md px-3 py-2.5 mt-1 focus:outline-none focus:ring-2 focus:ring-klein/20 focus:border-klein" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-mono-label text-[11px] uppercase tracking-widest text-zinc-400">Assigné à</label>
                  <select data-testid="ticket-assignee-select" value={ticketDraft.assigned_to || ""} onChange={(e) => setTicketDraft({ ...ticketDraft, assigned_to: e.target.value })} className="w-full border border-white/10 rounded-md px-3 py-2.5 mt-1 bg-background text-sm focus:outline-none focus:ring-2 focus:ring-klein/20 focus:border-klein">
                    <option value="">— Personne —</option>
                    {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="font-mono-label text-[11px] uppercase tracking-widest text-zinc-400">Priorité</label>
                  <select data-testid="ticket-priority-select" value={ticketDraft.priority || "medium"} onChange={(e) => setTicketDraft({ ...ticketDraft, priority: e.target.value })} className="w-full border border-white/10 rounded-md px-3 py-2.5 mt-1 bg-background text-sm focus:outline-none focus:ring-2 focus:ring-klein/20 focus:border-klein">
                    <option value="low">Basse</option><option value="medium">Moyenne</option><option value="high">Haute</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="font-mono-label text-[11px] uppercase tracking-widest text-zinc-400">Client</label>
                  <select data-testid="ticket-client-select" value={(ticketDraft.client_email && clientOpts.some((c) => c.email === ticketDraft.client_email)) ? ticketDraft.client_email : (ticketDraft.client_email ? "__manual__" : "")} onChange={(e) => { if (e.target.value !== "__manual__") setTicketDraft({ ...ticketDraft, client_email: e.target.value }); }} disabled={!ticketDraft.company_id} className="w-full border border-white/10 rounded-md px-3 py-2.5 mt-1 bg-background text-sm focus:outline-none focus:ring-2 focus:ring-klein/20 focus:border-klein disabled:opacity-50">
                    <option value="">{ticketDraft.company_id ? "— Choisir —" : "Choisir une entreprise"}</option>
                    {clientOpts.map((c) => <option key={c.email} value={c.email}>{c.name ? `${c.name} · ${c.email}` : c.email}</option>)}
                    {ticketDraft.client_email && !clientOpts.some((c) => c.email === ticketDraft.client_email) && <option value="__manual__">{`\u270F\uFE0F ${ticketDraft.client_email}`}</option>}
                  </select>
                </div>
                <div>
                  <label className="font-mono-label text-[11px] uppercase tracking-widest text-zinc-400">Autre adresse (optionnel)</label>
                  <input data-testid="ticket-client-manual" value={ticketDraft.client_email || ""} onChange={(e) => setTicketDraft({ ...ticketDraft, client_email: e.target.value })} placeholder="adresse non listée" className="w-full border border-white/10 rounded-md px-3 py-2.5 mt-1 bg-background text-sm focus:outline-none focus:ring-2 focus:ring-klein/20 focus:border-klein" />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setTicketDraft(null)} className="px-4 py-2 rounded-md border border-white/10 text-sm font-medium">Annuler</button>
            <button data-testid="create-ticket-button" onClick={createTicketFromEmail} className="px-4 py-2 rounded-md bg-klein text-zinc-950 text-sm font-medium">Créer le ticket</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
